import { motion } from "framer-motion";
import { Lock, Trash2 } from "lucide-react";
import { useState } from "react";
import Card from "../components/ui/Card";
import Toggle from "../components/ui/Toggle";
import { useAppState } from "../context/AppStateContext";

export default function SettingsPage() {
  const { state, actions } = useAppState();
  const [showPINSetup, setShowPINSetup] = useState(false);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pinError, setPinError] = useState("");

  const handleWipeData = () => {
    const confirmed = window.confirm(
      "Are you sure you want to wipe all data? This action cannot be undone."
    );
    if (confirmed) {
      actions.resetApp();
    }
  };

  const handleSetPin = () => {
    // Validation
    if (!pin || !confirmPin) {
      setPinError("Please enter both fields");
      return;
    }
    if (pin !== confirmPin) {
      setPinError("PINs do not match");
      return;
    }
    if (pin.length !== 4) {
      setPinError("PIN must be 4 digits");
      return;
    }
    if (!/^\d+$/.test(pin)) {
      setPinError("PIN must contain only numbers");
      return;
    }

    try {
      // Persist PIN to storage
      localStorage.setItem("appPin", pin);
      
      // Update settings context with both PIN and enable privacy lock
      actions.setSetting("appPin", pin);
      actions.setSetting("privacyLock", true);
      
      setShowPINSetup(false);
      setPin("");
      setConfirmPin("");
      setPinError("");
    } catch (error) {
      setPinError("Failed to save PIN: " + error.message);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* Privacy & Security */}
      <Card title="Privacy & Security" icon={Lock}>
        <div className="space-y-4">
          <Toggle
            value={state.settings?.privacyLock ?? false}
            onChange={(v) => {
              if (v && !state.settings?.appPin) {
                // Require PIN setup before enabling lock
                setShowPINSetup(true);
                setPinError("");
              } else {
                actions.setSetting("privacyLock", v);
              }
            }}
            label="App Lock"
            hint={state.settings?.appPin 
              ? "Adds a 4-digit PIN lock screen when enabled." 
              : "Set a PIN first to enable app lock."}
          />
          <Toggle
            value={state.settings?.hideSensitiveText ?? false}
            onChange={(v) => actions.setSetting("hideSensitiveText", v)}
            label="Discreet Mode"
            hint="Uses softer labels and masked wording throughout."
          />
        </div>
      </Card>

      {/* Storage */}
      <Card title="Storage" icon={Trash2}>
        <div className="space-y-3">
          <p className="text-sm text-zinc-400">
            All your data is stored locally on this device. No cloud sync in this MVP.
          </p>
          <button
            onClick={handleWipeData}
            className="w-full rounded-xl border border-rose-500/30 text-rose-500 py-3 font-semibold hover:bg-rose-950/20 transition-all active:scale-95 min-h-[44px]"
          >
            Wipe All Data
          </button>
          <p className="text-xs text-zinc-600">
            This will permanently delete all streaks, prayers, notes, and settings.
          </p>
        </div>
      </Card>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="pt-4 border-t border-zinc-800"
      >
        <div className="text-center space-y-2">
          <div className="text-xs text-zinc-600 uppercase tracking-widest font-semibold">
            Version 1.0.0
          </div>
          <div className="text-xs text-zinc-500">
            Developed by HM
          </div>
        </div>
      </motion.div>

      {/* PIN Setup Modal */}
      {showPINSetup && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#18181B] rounded-2xl border border-zinc-800 p-6 w-full max-w-sm space-y-4"
          >
            <h2 className="text-lg font-bold text-white">Set App Lock PIN</h2>
            <p className="text-sm text-zinc-400">Create a 4-digit PIN to secure your app.</p>

            {/* Error message */}
            {pinError && (
              <div className="p-3 bg-red-900/30 border border-red-700 rounded text-red-300 text-sm">
                {pinError}
              </div>
            )}

            <div className="space-y-3">
              <input
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={(e) => {
                  const val = e.target.value.slice(0, 4);
                  if (/^\d*$/.test(val)) {
                    setPin(val);
                    setPinError("");
                  }
                }}
                placeholder="Enter 4-digit PIN"
                className="w-full rounded-xl bg-[#09090B] border border-zinc-800 text-zinc-300 px-3 py-3 text-base text-center tracking-[0.2em] outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent min-h-[44px]"
                maxLength="4"
              />
              <input
                type="password"
                inputMode="numeric"
                value={confirmPin}
                onChange={(e) => {
                  const val = e.target.value.slice(0, 4);
                  if (/^\d*$/.test(val)) {
                    setConfirmPin(val);
                    setPinError("");
                  }
                }}
                placeholder="Confirm PIN"
                className="w-full rounded-xl bg-[#09090B] border border-zinc-800 text-zinc-300 px-3 py-3 text-base text-center tracking-[0.2em] outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent min-h-[44px]"
                maxLength="4"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setShowPINSetup(false);
                  setPin("");
                  setConfirmPin("");
                  setPinError("");
                }}
                className="rounded-xl py-3 font-semibold bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700 transition-all active:scale-95 min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={handleSetPin}
                disabled={pin.length !== 4 || confirmPin.length !== 4}
                className="rounded-xl py-3 font-semibold bg-[#10B981] text-zinc-900 ring-1 ring-[#10B981]/50 glow-mint hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 min-h-[44px]"
              >
                Confirm
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}